import type { PermissionProps } from "@qendo/permissions";
import type {
    ReportableTrackable,
    ReportData,
    ReportRequestData,
    ReportSelectorData,
    ReportSummaryData,
} from "@qendo/profile/reports";
import type { ListData } from "@qendo/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useApi } from "~/providers/ApiProvider";
import { useTrackable } from "~/providers/TrackableProvider";
import { formatDateString } from "~/util/dates";

declare type ReportState = {
    reports: ReportSelectorData[];
    refreshReports(): Promise<void>;
    getReport(report: ReportRequestData): Promise<ReportData>;
};

export const ReportContext = createContext<ReportState>({
    reports: [],
    refreshReports: async () => {
        return;
    },
    getReport: async (report) => ({
        period: report.period,
        trackable_id: "none",
        average: 0,
        start_at: formatDateString(new Date()),
        end_at: formatDateString(new Date()),
        points: [],
    }),
});

export function useReport() {
    const value = useContext(ReportContext);
    if (!value) {
        throw new Error("useReport must be wrapped in a <ReportProvider />");
    }
    return value;
}

export type ReportProviderProps = {
    profile_id: string | null;
    children: ReactNode;
} & PermissionProps;

export function ReportProvider({ profile_id, canAny, children }: ReportProviderProps) {
    const queryClient = useQueryClient();
    const { findTrackable } = useTrackable();
    const { client } = useApi();

    const queryKey = ["reports", profile_id];

    const reportsQuery = useQuery({
        queryKey,
        queryFn: async () => {
            if (canAny("view", ["symptoms", "activities"])) {
                return await client()
                    .get(`profiles/${profile_id}/reports`)
                    .then((response: AxiosResponse<ListData<ReportSummaryData>>) => {
                        return response.data.data;
                    })
                    .catch(() => []);
            }

            return [];
        },
        staleTime: 1000 * 60 * 60, // 1 hour,
        placeholderData: [],
        refetchOnMount: true,
    });

    const reports = useMemo(
        () =>
            (reportsQuery.data ?? []).map<ReportSelectorData>((report) => ({
                ...(findTrackable(report.trackable_id) as ReportableTrackable),
                ...report,
            })),
        [reportsQuery.data, findTrackable],
    );

    const refreshReports = async () => {
        await queryClient.invalidateQueries({ queryKey });
    };

    const getReport = async ({ trackable_id, ...params }: ReportRequestData) => {
        return await client()
            .get(`profiles/${profile_id}/reports/${trackable_id}`, { params })
            .then((response: AxiosResponse<ReportData>) => response.data);
    };

    const value = {
        reports,
        refreshReports,
        getReport,
    };

    return <ReportContext.Provider value={value}>{children}</ReportContext.Provider>;
}
